// src/components/PromoModal/PromoModal.jsx
import { ReusableModal } from "@/components/UI/Modals/CustomModals";
import {
  Form,
  Radio,
  Input,
  Button,
  Checkbox,
  DatePicker,
  Select,
  Spin,
  message,
  Switch, // Keep Switch
  InputNumber,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import {
  InboxOutlined,
  TagOutlined,
  ReloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  useAddPromoMutation,
  useUpdatePromoMutation,
  useGetPromoTypeQuery,
  useAddPromoTypeMutation,
  useGetRanksQuery,
} from "@/services/api/promo/promoApi";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";
import useAuth from "@/hooks/useAuth";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const PromoModal = ({ open, onClose, mode, initialData }) => {
  const [form] = Form.useForm();
  const [promoTypeForm] = Form.useForm();
  const user = useAuth();

  const { data: locations = [], isLoading: locationLoading } =
    useGetLocationsQuery({ skip: !open });
  const { data: promoTypes = [], isLoading: promoTypeLoading } =
    useGetPromoTypeQuery({ skip: !open });
  const { data: ranks = [], isLoading: ranksLoading } = useGetRanksQuery({
    skip: !open,
  });
  const [createPromo, { isLoading: creating }] = useAddPromoMutation();
  const [updatePromo, { isLoading: updating }] = useUpdatePromoMutation();
  const [addPromoType, { isLoading: addingPromoType }] =
    useAddPromoTypeMutation();

  const [promoType, setPromoType] = useState("");
  const [fileList, setFileList] = useState([]);
  const [dontSetDuration, setDontSetDuration] = useState(false);
  const [dontLimitUsers, setDontLimitUsers] = useState(false);
  const [showAddPromoType, setShowAddPromoType] = useState(false);

  // New state variables for is_global and status
  const [isGlobal, setIsGlobal] = useState(false);
  const [promoStatus, setPromoStatus] = useState(false);

  // Auto-generate promo code
  const generatePromoCode = () => {
    const promoName = form.getFieldValue("name");
    if (!promoName) {
      message.warning("Please enter a promo name first");
      return;
    }
    const namePrefix = promoName
      .replace(/[^a-zA-Z0-9]/g, "")
      .substring(0, 4)
      .toUpperCase();
    const randomNumber = Math.floor(1000 + Math.random() * 9000);
    form.setFieldsValue({ code: `${namePrefix}${randomNumber}` });
  };

  // Reset and populate form & local states
  useEffect(() => {
    if (!open) return;

    form.resetFields();
    promoTypeForm.resetFields();
    setFileList([]);
    setPromoType("");
    setDontSetDuration(false);
    setDontLimitUsers(false);
    setShowAddPromoType(false);

    // Initialize local states for switches
    setIsGlobal(false);
    setPromoStatus(false);

    if (mode === "add") {
      // Set default values for add mode if needed by form (not switches here)
      // Switches are already initialized to false above
    }

    if ((mode === "view" || mode === "edit") && initialData) {
      const extractId = (field, nestedField) => field || nestedField?.id;

      const formData = {
        ...initialData,
        promo_type_id: extractId(
          initialData.promo_type_id,
          initialData.promo_type
        )?.toString(),
        location_id: extractId(initialData.location_id, initialData.location),
        rank_id: extractId(initialData.rank_id, initialData.rank),
        duration:
          initialData.start_date && initialData.end_date
            ? [dayjs(initialData.start_date), dayjs(initialData.end_date)]
            : undefined,
        // is_global and status are now managed by local states, remove from formData for form
        // We'll update the local states directly below
      };

      form.setFieldsValue(formData);
      setPromoType(formData.promo_type_id || "");
      setDontSetDuration(!initialData.start_date && !initialData.end_date);
      setDontLimitUsers(!initialData.usage_limit);

      // Update local states for switches directly from initialData
      setIsGlobal(!!initialData.is_global);
      setPromoStatus(!!initialData.status);

      if (initialData.imageUrl) {
        setFileList([
          {
            uid: "-1",
            name: "promo-image",
            status: "done",
            url: initialData.imageUrl,
          },
        ]);
      }
    }
  }, [open, mode, initialData, form, promoTypeForm]);

  // Upload config
  const uploadProps = useMemo(
    () => ({
      name: "file",
      multiple: false,
      maxCount: 1,
      accept: ".jpg,.jpeg,.png",
      fileList,
      beforeUpload: (file) => {
        const isValidType = ["image/jpeg", "image/jpg", "image/png"].includes(
          file.type
        );
        const isLt1M = file.size / 1024 / 1024 < 1;
        if (!isValidType) message.error("You can only upload JPG/PNG files!");
        if (!isLt1M) message.error("Image must be smaller than 1MB!");
        return false;
      },
      onChange: (info) => setFileList(info.fileList),
      onRemove: () => setFileList([]),
    }),
    [fileList]
  );

  // Options
  const promoTypeOptions = useMemo(
    () =>
      promoTypes.map((pt) => ({
        key: pt.id.toString(),
        value: pt.id.toString(),
        label: pt.name,
        description: pt.description,
      })),
    [promoTypes]
  );

  const rankOptions = useMemo(
    () => ranks.map((r) => ({ value: r.id, label: r.name })),
    [ranks]
  );

  const locationOptions = useMemo(
    () =>
      locations.map((loc) => ({
        value: loc.id,
        label: `${loc.name} - ${loc.state.name}`,
      })),
    [locations]
  );

  // Handle add promo type
  const handleAddPromoType = async (values) => {
    try {
      await addPromoType(values).unwrap();
      message.success("Promo type added successfully!");
      promoTypeForm.resetFields();
      setShowAddPromoType(false);
    } catch (err) {
      message.error(err?.data?.message || "Failed to add promo type");
    }
  };

  // Form submit
  const handleFinish = async (values) => {
    const { duration, ...rest } = values;

    const payload = {
      ...rest,
      promo_type_id: rest.promo_type_id
        ? Number(rest.promo_type_id)
        : undefined,
      start_date:
        dontSetDuration || !duration ? null : duration[0].format("YYYY-MM-DD"),
      end_date:
        dontSetDuration || !duration ? null : duration[1].format("YYYY-MM-DD"),
      usage_limit: dontLimitUsers ? null : rest.usage_limit,
      image: fileList[0]?.originFileObj || null,
      created_by: mode === "add" ? user?.id : undefined,
      updated_by: mode !== "add" ? user?.id : undefined,
      // Manually add the values from our local states for switches
      is_global: isGlobal,
      status: promoStatus,
    };

    // Clean undefined values
    Object.keys(payload).forEach(
      (key) =>
        (payload[key] === undefined || payload[key] === null) &&
        delete payload[key]
    );

    try {
      if (mode === "add") {
        await createPromo(payload).unwrap();
        message.success("Promo created successfully!");
      } else {
        await updatePromo({ id: initialData.id, ...payload }).unwrap();
        message.success("Promo updated successfully!");
      }
      onClose();
    } catch (err) {
      console.error("Failed to submit promo:", err);
      message.error(err?.data?.message || "Something went wrong");
    }
  };

  const renderContent = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      disabled={mode === "view"}
      // initialValues are not needed for switches here since they are handled by local state
    >
      {/* File Upload */}
      <Dragger {...uploadProps} disabled={mode === "view"} className="mb-6 p-5">
        <p className="ant-upload-drag-icon">
          <InboxOutlined className="text-5xl text-blue-500" />
        </p>
        <p className="ant-upload-text text-sm font-medium text-gray-600">
          Drag and Drop Files here or
        </p>
        <p className="ant-upload-hint mt-2">
          <span className="inline-block px-4 py-1.5 border border-gray-300 rounded-md text-sm font-medium cursor-pointer bg-white">
            Browse Files
          </span>
        </p>
        <p className="ant-upload-hint text-xs text-gray-500 mt-3">
          (Max. file size: 1 MB; .jpg, .jpeg, .png)
        </p>
      </Dragger>

      {/* Promo Information */}
      <div className="mb-6">
        <h3 className="text-base font-semibold mb-4">Promo Information</h3>

        <Form.Item
          label={<span className="text-xs text-gray-500">Promo URL</span>}
          name="url"
        >
          <Input placeholder="Enter promo URL" size="large" />
        </Form.Item>

        <Form.Item
          label={<span className="text-xs text-gray-500">Promo Name</span>}
          name="name"
          rules={
            mode === "view"
              ? []
              : [{ required: true, message: "Enter promo name" }]
          }
        >
          <Input placeholder="Enter promo name" size="large" />
        </Form.Item>

        <Form.Item
          label={<span className="text-xs text-gray-500">Promo Code</span>}
          name="code"
          rules={
            mode === "view"
              ? []
              : [{ required: true, message: "Enter promo code" }]
          }
        >
          <Input
            placeholder="Enter promo code"
            size="large"
            suffix={
              mode !== "view" && (
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={generatePromoCode}
                  className="text-blue-500 hover:text-blue-600"
                />
              )
            }
          />
        </Form.Item>

        <Form.Item
          label={
            <span className="text-xs text-gray-500">Promo Description</span>
          }
          name="description"
          rules={
            mode === "view"
              ? []
              : [{ required: true, message: "Enter description" }]
          }
        >
          <TextArea placeholder="Enter promo description" rows={3} />
        </Form.Item>
      </div>

      {/* Promo Type */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold">Promo type</h3>
          {mode !== "view" && (
            <Checkbox
              checked={showAddPromoType}
              onChange={(e) => setShowAddPromoType(e.target.checked)}
            >
              <span className="text-sm text-gray-600">Add promo type</span>
            </Checkbox>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Type of coupon you want to create
        </p>

        {/* Add Promo Type Section */}
        {showAddPromoType && mode !== "view" && (
          <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <PlusOutlined className="text-blue-500" />
              Add New Promo Type
            </h4>

            <Form.Item
              label={<span className="text-xs text-gray-500">Name</span>}
              className="mb-3"
              name={["promoTypeForm", "name"]}
              rules={[{ required: true, message: "Enter promo type name" }]}
            >
              <Input placeholder="Enter promo type name" size="large" />
            </Form.Item>

            <Form.Item
              label={<span className="text-xs text-gray-500">Description</span>}
              className="mb-3"
              name={["promoTypeForm", "description"]}
              rules={[
                { required: true, message: "Enter promo type description" },
              ]}
            >
              <TextArea placeholder="Enter promo type description" rows={3} />
            </Form.Item>

            <div className="flex justify-end gap-2">
              <Button
                onClick={() => {
                  promoTypeForm.resetFields();
                  setShowAddPromoType(false);
                }}
                disabled={addingPromoType}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={addingPromoType}
                className="bg-blue-500 hover:bg-blue-600"
                onClick={async () => {
                  try {
                    const values = await promoTypeForm.validateFields();
                    await handleAddPromoType(values);
                  } catch (errorInfo) {
                    message.error(
                      "Please fill in all required fields for promo type."
                    );
                  }
                }}
              >
                Save Promo Type
              </Button>
            </div>
          </div>
        )}

        <Form.Item
          name="promo_type_id"
          rules={
            mode === "view"
              ? []
              : [{ required: true, message: "Select a promo type" }]
          }
        >
          <Radio.Group
            className="w-full"
            onChange={(e) => setPromoType(e.target.value)}
            disabled={mode === "view"}
          >
            <div className="grid grid-cols-3 gap-3">
              {promoTypeLoading ? (
                <div className="col-span-3 flex justify-center py-8">
                  <Spin />
                </div>
              ) : (
                promoTypeOptions.map((opt) => (
                  <Radio.Button
                    key={opt.key}
                    value={opt.value}
                    title={opt.description}
                    className={`h-auto p-4 text-center border-2 rounded-lg flex flex-col items-center justify-center ${
                      promoType === opt.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                        promoType === opt.value
                          ? "bg-blue-200 text-blue-500"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <TagOutlined className="text-xl" />
                    </div>
                    <span className="text-[13px] font-medium text-gray-800 leading-tight">
                      {opt.label}
                    </span>
                  </Radio.Button>
                ))
              )}
            </div>
          </Radio.Group>
        </Form.Item>
      </div>

      {/* Discount Settings */}
      <div className="mb-6">
        <h3 className="text-base font-semibold mb-4">Discount Settings</h3>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label={<span className="text-xs text-gray-500">Discount Type</span>}
            name="discount_type"
            rules={
              mode === "view"
                ? []
                : [{ required: true, message: "Select discount type" }]
            }
          >
            <Select
              placeholder="Select discount type"
              size="large"
              disabled={mode === "view"}
              options={[
                { value: "percentage", label: "Percentage" },
                { value: "fixed", label: "Fixed Amount" },
              ]}
            />
          </Form.Item>

          <Form.Item
            label={
              <span className="text-xs text-gray-500">Discount Value</span>
            }
            name="discount_value"
            rules={
              mode === "view"
                ? []
                : [{ required: true, message: "Enter discount value" }]
            }
          >
            <InputNumber
              placeholder="Enter value"
              size="large"
              className="w-full"
              min={0}
              disabled={mode === "view"}
            />
          </Form.Item>
        </div>
      </div>

      {/* Location & Rank */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Form.Item
          label={<span className="text-xs text-gray-500">Location</span>}
          name="location_id"
        >
          <Select
            placeholder="Select location"
            size="large"
            loading={locationLoading}
            showSearch
            disabled={mode === "view"}
            filterOption={(input, option) =>
              option?.label.toLowerCase().includes(input.toLowerCase())
            }
            options={locationOptions}
          />
        </Form.Item>

        <Form.Item
          label={<span className="text-xs text-gray-500">Rank</span>}
          name="rank_id"
        >
          <Select
            placeholder="Select rank"
            size="large"
            loading={ranksLoading}
            showSearch
            disabled={mode === "view"}
            filterOption={(input, option) =>
              option?.label.toLowerCase().includes(input.toLowerCase())
            }
            options={rankOptions}
          />
        </Form.Item>
      </div>

      {/* Order Type */}
      <Form.Item
        label={
          <span className="text-xs text-gray-500">Order Type (Optional)</span>
        }
        name="order_type_id"
      >
        <Select
          placeholder="Select order type"
          size="large"
          disabled={mode === "view"}
          options={[
            { value: 1, label: "Bulk Order" },
            { value: 2, label: "Catering Order" },
          ]}
        />
      </Form.Item>

      {/* Duration & Usage Limits */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <Checkbox
            checked={dontSetDuration}
            onChange={(e) => {
              setDontSetDuration(e.target.checked);
              if (e.target.checked) {
                form.setFieldsValue({ duration: undefined });
              }
            }}
            className="mb-2"
            disabled={mode === "view"}
          >
            <span className="text-sm text-gray-600">Don't set duration</span>
          </Checkbox>
          <Form.Item
            label={<span className="text-xs text-gray-500">Duration</span>}
            name="duration"
            className="mb-0"
          >
            <RangePicker
              size="large"
              className="w-full"
              disabled={dontSetDuration || mode === "view"}
              format="MMM DD, YYYY"
            />
          </Form.Item>
        </div>

        <div>
          <Checkbox
            checked={dontLimitUsers}
            onChange={(e) => {
              setDontLimitUsers(e.target.checked);
              if (e.target.checked) {
                form.setFieldsValue({ usage_limit: undefined });
              }
            }}
            className="mb-2"
            disabled={mode === "view"}
          >
            <span className="text-sm text-gray-600">
              Don't limit total usage
            </span>
          </Checkbox>
          <Form.Item
            label={<span className="text-xs text-gray-500">Usage Limit</span>}
            name="usage_limit"
            className="mb-0"
          >
            <InputNumber
              placeholder="Enter limit"
              size="large"
              className="w-full"
              min={1}
              disabled={dontLimitUsers || mode === "view"}
            />
          </Form.Item>
        </div>
      </div>

      {/* Per User Limit */}
      <Form.Item
        label={<span className="text-xs text-gray-500">Per User Limit</span>}
        name="per_user_limit"
      >
        <InputNumber
          placeholder="Enter per user limit"
          size="large"
          className="w-full"
          min={1}
          disabled={mode === "view"}
        />
      </Form.Item>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Global Promo Switch */}
        <Form.Item
          label={<span className="text-xs text-gray-500">Global Promo</span>}
        >
          <Switch
            checked={isGlobal}
            onChange={setIsGlobal}
            disabled={mode === "view"}
          />
          <span className="text-sm text-gray-600 ml-2">
            Apply to all locations
          </span>
        </Form.Item>

        {/* Status Switch */}
        <Form.Item
          label={<span className="text-xs text-gray-500">Status</span>}
        >
          <Switch
            checked={promoStatus} // Controlled by local state
            onChange={setPromoStatus} // Update local state
            disabled={mode === "view"}
          />
          <span className="text-sm text-gray-600 ml-2">Active</span>
        </Form.Item>
      </div>

      {/* Usage Count (View/Edit only) */}
      {mode !== "add" && initialData?.usage_count !== undefined && (
        <Form.Item
          label={<span className="text-xs text-gray-500">Usage Count</span>}
        >
          <Input
            value={initialData.usage_count}
            size="large"
            disabled
            className="bg-gray-50"
          />
        </Form.Item>
      )}
    </Form>
  );

  const renderFooter = () => {
    if (mode === "view") {
      return (
        <div className="flex justify-end">
          <Button onClick={onClose} size="large">
            Close
          </Button>
        </div>
      );
    }

    return (
      <div className="flex justify-end gap-4">
        <Button onClick={onClose} size="large" disabled={creating || updating}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={creating || updating}
          size="large"
          className="bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:from-[#F9A022] hover:to-[#D06D01] border-0"
        >
          {mode === "add" ? "Create Promo" : "Update Promo"}
        </Button>
      </div>
    );
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title={
        mode === "view"
          ? "View Promo"
          : mode === "edit"
          ? "Edit Promo"
          : "Add Promo / Discount"
      }
      subtitle={
        mode === "view"
          ? "Promo details"
          : mode === "edit"
          ? "Update promo information"
          : "Set up promo codes or discount"
      }
      form={form}
      customFooter
      footer={renderFooter()}
      position="right"
      slideDirection="right"
      animationDuration={400}
      width={700}
    >
      {locationLoading || ranksLoading || promoTypeLoading ? (
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        renderContent()
      )}
    </ReusableModal>
  );
};

export default PromoModal;
